import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import './Styles.css';
import TeamHeaderMenu from './Menu/TeamHeaderMenu';
import { formatMessage } from '../../../../Localization/formatMessage';
import Messages from '../../../../Localization/Messages';
import { useLegalUnitName } from '../../../../Helpers/CustomHooks';
import { getTeamHeaderItems } from './Functions';
import { Ui16ArrowSmDown, Ui16ArrowSmUp } from '@ids/react-icons';
import { useGlobalContext } from '../../../../context/GlobalContext';

const TeamHeader = ({ team }) => {
  const legalUnitName = useLegalUnitName(team);
  const { isTestEnvironment } = useGlobalContext();

  const rowRef = useRef(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleItemCount, setVisibleItemCount] = useState(0);

  const items = useMemo(
    () =>
      getTeamHeaderItems({
        team,
        legalUnitName,
        isTestEnvironment,
      }),
    [team, legalUnitName, isTestEnvironment]
  );

  useLayoutEffect(() => {
    const recalculateLayout = () => {
      let itemWidthSum = 0;
      for (let i = 0; i < items.length; i++) {
        if (itemWidthSum + items[i].width > rowRef.current.offsetWidth) {
          setVisibleItemCount(i);
          return;
        }
        itemWidthSum += items[i].width + 10;
      }

      setVisibleItemCount(items.length);
    };

    recalculateLayout();
    window.addEventListener('resize', recalculateLayout);

    return () => {
      window.removeEventListener('resize', recalculateLayout);
    };
  }, [items]);

  const getMaxWidth = () => {
    const itemsWidth = items.reduce((sum, item) => sum + item.width + 10, 0);
    return itemsWidth + 80 + 48;
    };

  return (
    <div className={`teamPage-header-container`} style={{ maxWidth: `${getMaxWidth()}px` }}>
      <div className="teamPage-headerContainer-firstItem">
        <img aria-hidden className="if size-32" src="/assets/people.svg" alt="Team" />
      </div>

      <div className="teamPage-header-itemContainer" ref={rowRef}>
        <div className="teamPage-headerContainer-items teamPage-headerContainer-items--firstRow">
          {items.slice(0, visibleItemCount).map(item => (
              <div key={item.key} className="teamPage-headerItem" style={{ width: `${item.width}px` }}>
                  <div className="teamPage-headerContainer-itemTitle">{item.title}</div>
                  <div className={item.value ?? "teamPage-headerContainer-itemValue-notFound"}>
                      {!item.value ? formatMessage(Messages.common_missingValue) : item.value}</div>
              </div>
          ))}
          {isExpanded && items.length > visibleItemCount && (
            <>
              {items.slice(visibleItemCount).map(item => (
                <div
                  key={item.key}
                  className="teamPage-headerItem"
                  style={{ width: `${item.width}px`, minWidth: `${item.width}px` }}>
                  <div className="teamPage-headerContainer-itemTitle">{item.title}</div>
                      <div className={item.value ?? "teamPage-headerContainer-itemValue-notFound"}>
                          {!item.value ? formatMessage(Messages.common_missingValue) : item.value}</div>
                </div>
              ))}
            </>
          )}
        </div>

        <div
          className={`teamPage-headerContainer-showMore ${items.length > visibleItemCount ? 'clickable' : ''}`}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}>
          {items.length > visibleItemCount && (
            <>
              {isExpanded ? (
                <>
                  {formatMessage(Messages.teamPage_teamHeader_showLess)} <Ui16ArrowSmUp />
                </>
              ) : (
                <>
                  {formatMessage(Messages.teamPage_teamHeader_showMore)} <Ui16ArrowSmDown />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="teamPage-headerContainer-lastItem">
        <TeamHeaderMenu team={team} />
      </div>
    </div>
  );
};

export default TeamHeader;
